import type { ComponentType, SVGProps } from 'react';
import {
  CalendarIcon,
  ChartBarIcon,
  ClipboardDocumentListIcon,
  CodeBracketIcon,
  Cog6ToothIcon,
  DocumentMagnifyingGlassIcon,
  DocumentTextIcon,
  HomeIcon,
  LockClosedIcon,
  MapIcon,
  QuestionMarkCircleIcon,
  RectangleStackIcon,
  ServerIcon,
  ShieldCheckIcon,
  UserGroupIcon,
  UsersIcon,
  BookOpenIcon,
} from '@heroicons/react/24/outline';

export type NavigationIcon = ComponentType<SVGProps<SVGSVGElement>>;

export interface NavigationItem {
  name: string;
  href: string;
  icon: NavigationIcon;
  permission?: string;
  roles?: string[];
  blueDotId?: string;
  navClass?: string;
}

export interface NavigationSection {
  id: string;
  title: string;
  items: NavigationItem[];
}

export interface PageMeta {
  title: string;
  description: string;
  eyebrow?: string;
}

export const navigationSections: NavigationSection[] = [
  {
    id: 'overview',
    title: 'Overview',
    items: [
      { name: 'Dashboard', href: '/', icon: HomeIcon, navClass: 'nav-dashboard' },
      { name: 'Calendar', href: '/calendar', icon: CalendarIcon, navClass: 'nav-calendar' },
      { name: 'Releases', href: '/releases', icon: LockClosedIcon, permission: 'release:read', navClass: 'nav-releases' },
      { name: 'Reports', href: '/reports', icon: DocumentTextIcon, permission: 'reports:read', navClass: 'nav-reports' },
    ],
  },
  {
    id: 'delivery',
    title: 'Work Management',
    items: [
      { name: 'Tasks', href: '/tasks', icon: ClipboardDocumentListIcon, navClass: 'nav-tasks' },
      { name: 'Projects', href: '/projects', icon: ChartBarIcon, navClass: 'nav-projects' },
      { name: 'Team', href: '/team', icon: UserGroupIcon, permission: 'users:read', navClass: 'nav-team' },
    ],
  },
  {
    id: 'security',
    title: 'Security',
    items: [
      { name: 'Security', href: '/security', icon: ShieldCheckIcon, permission: 'security:read', navClass: 'nav-security' },
      { name: 'Scanner', href: '/scanner', icon: DocumentMagnifyingGlassIcon, permission: 'security:read', navClass: 'nav-scanner' },
      { name: 'SAST', href: '/sast', icon: CodeBracketIcon, permission: 'security:read', navClass: 'nav-sast' },
      { name: 'Secrets', href: '/secrets', icon: LockClosedIcon, permission: 'security:read', navClass: 'nav-secrets' },
      { name: 'Compliance', href: '/compliance', icon: ShieldCheckIcon, permission: 'compliance:read', navClass: 'nav-compliance' },
    ],
  },
  {
    id: 'admin',
    title: 'Administration',
    items: [
      { name: 'Users', href: '/users', icon: UsersIcon, permission: 'users:write', navClass: 'nav-users' },
      { name: 'CMDB', href: '/cmdb', icon: ServerIcon, permission: 'projects:read', navClass: 'nav-cmdb' },
      { name: 'Integrations', href: '/integrations/issue-trackers', icon: RectangleStackIcon, permission: 'admin:write', navClass: 'nav-integrations' },
      { name: 'Health', href: '/health', icon: ServerIcon, navClass: 'nav-health' },
      { name: 'Settings', href: '/settings', icon: Cog6ToothIcon, navClass: 'nav-settings' },
    ],
  },
  {
    id: 'support',
    title: 'Help',
    items: [
      { name: 'Help', href: '/help', icon: QuestionMarkCircleIcon, blueDotId: 'nav-help', navClass: 'nav-help' },
      { name: 'Glossary', href: '/glossary', icon: BookOpenIcon, blueDotId: 'nav-glossary', navClass: 'nav-glossary' },
      { name: 'User Journeys', href: '/user-journey', icon: MapIcon, blueDotId: 'nav-journey', navClass: 'nav-journey' },
    ],
  },
];

const routeMetaEntries: Array<{ path: string; meta: PageMeta }> = [
  {
    path: '/integrations/issue-trackers',
    meta: {
      title: 'Integrations',
      description: 'Configure issue tracker connections, sync rules, and account-level delivery settings.',
      eyebrow: 'Administration',
    },
  },
  {
    path: '/secrets-detection/config',
    meta: {
      title: 'Secrets Detection',
      description: 'Tune secret scanning behavior, exclusions, and operational defaults.',
      eyebrow: 'Security',
    },
  },
  {
    path: '/secrets-detection/patterns',
    meta: {
      title: 'Custom Patterns',
      description: 'Manage custom detectors used by the secrets scanning workflow.',
      eyebrow: 'Security',
    },
  },
  {
    path: '/security-dashboard',
    meta: {
      title: 'Security Dashboard',
      description: 'Track posture, remediation progress, and risk trends across your delivery surface.',
      eyebrow: 'Security',
    },
  },
  {
    path: '/compliance-snapshots',
    meta: {
      title: 'Compliance Snapshots',
      description: 'Compare historical compliance states and export audit-ready evidence.',
      eyebrow: 'Security',
    },
  },
  {
    path: '/dependency-insights',
    meta: {
      title: 'Dependency Insights',
      description: 'Inspect vulnerable libraries, license exposure, and remediation priority.',
      eyebrow: 'Security',
    },
  },
  {
    path: '/exceptions',
    meta: {
      title: 'Exception Workflow',
      description: 'Review approved risk exceptions and the controls attached to each decision.',
      eyebrow: 'Security',
    },
  },
  {
    path: '/notifications',
    meta: {
      title: 'Notifications',
      description: 'Review in-app alerts, dismiss stale items, and jump back into related work.',
      eyebrow: 'Inbox',
    },
  },
  {
    path: '/profile',
    meta: {
      title: 'Profile',
      description: 'Manage your personal account settings, identity details, and preferences.',
      eyebrow: 'Account',
    },
  },
  {
    path: '/calendar',
    meta: {
      title: 'Calendar',
      description: 'Track scheduled work, delivery milestones, and operational deadlines.',
      eyebrow: 'Overview',
    },
  },
  {
    path: '/releases',
    meta: {
      title: 'Releases',
      description: 'Coordinate release readiness, approvals, and delivery checkpoints.',
      eyebrow: 'Overview',
    },
  },
  {
    path: '/tasks',
    meta: {
      title: 'Tasks',
      description: 'Manage execution detail, ownership, and due dates without losing scanability.',
      eyebrow: 'Work Management',
    },
  },
  {
    path: '/projects',
    meta: {
      title: 'Projects',
      description: 'Review project status, exposure, and recent delivery activity.',
      eyebrow: 'Work Management',
    },
  },
  {
    path: '/security',
    meta: {
      title: 'Security',
      description: 'Monitor security posture, triage findings, and act on risk faster.',
      eyebrow: 'Security',
    },
  },
  {
    path: '/scanner',
    meta: {
      title: 'Scanner',
      description: 'Configure scans, track results, and manage intake from automated security tooling.',
      eyebrow: 'Security',
    },
  },
  {
    path: '/sast',
    meta: {
      title: 'SAST Scanner',
      description: 'Detect vulnerabilities in source code using static analysis powered by Semgrep.',
      eyebrow: 'Security',
    },
  },
  {
    path: '/secrets',
    meta: {
      title: 'Secrets',
      description: 'Investigate leaked credentials, patterns, and remediation activity.',
      eyebrow: 'Security',
    },
  },
  {
    path: '/compliance',
    meta: {
      title: 'Compliance',
      description: 'Monitor policy coverage, evidence readiness, and control effectiveness.',
      eyebrow: 'Security',
    },
  },
  {
    path: '/reports',
    meta: {
      title: 'Reports',
      description: 'Generate stakeholder reporting without losing the audit trail behind the numbers.',
      eyebrow: 'Overview',
    },
  },
  {
    path: '/team',
    meta: {
      title: 'Team',
      description: 'Understand capability, workload, and collaboration details across the delivery team.',
      eyebrow: 'Work Management',
    },
  },
  {
    path: '/users',
    meta: {
      title: 'Users',
      description: 'Manage access, roles, and account lifecycle with predictable admin flows.',
      eyebrow: 'Administration',
    },
  },
  {
    path: '/cmdb',
    meta: {
      title: 'CMDB',
      description: 'Inspect system inventory, change context, and service ownership data.',
      eyebrow: 'Administration',
    },
  },
  {
    path: '/health',
    meta: {
      title: 'Health',
      description: 'Review service health, operational checks, and environment status.',
      eyebrow: 'Administration',
    },
  },
  {
    path: '/settings',
    meta: {
      title: 'Settings',
      description: 'Configure account, system, and platform behavior from one coherent admin surface.',
      eyebrow: 'Administration',
    },
  },
  {
    path: '/help',
    meta: {
      title: 'Help',
      description: 'Find product guidance, walkthroughs, and usage support.',
      eyebrow: 'Help',
    },
  },
  {
    path: '/glossary',
    meta: {
      title: 'Glossary',
      description: 'Look up terms and product concepts without leaving the workspace.',
      eyebrow: 'Help',
    },
  },
  {
    path: '/user-journey',
    meta: {
      title: 'User Journeys',
      description: 'Review guided workflows and the intended end-to-end product experience.',
      eyebrow: 'Help',
    },
  },
  {
    path: '/',
    meta: {
      title: 'Dashboard',
      description: 'Track the delivery, security, and compliance signals that need attention today.',
      eyebrow: 'Overview',
    },
  },
];

export const isNavigationItemActive = (currentPath: string, href: string): boolean => {
  if (href === '/') {
    return currentPath === '/' || currentPath === '/dashboard';
  }

  return currentPath === href || currentPath.startsWith(`${href}/`);
};

export const getPageMeta = (pathname: string): PageMeta => {
  const orderedEntries = [...routeMetaEntries].sort((left, right) => right.path.length - left.path.length);
  const match = orderedEntries.find(({ path }) => isNavigationItemActive(pathname, path));

  if (match) {
    return match.meta;
  }

  return {
    title: 'Workspace',
    description: 'Manage software delivery, security posture, and admin operations from one place.',
    eyebrow: 'SDLC Tracker',
  };
};
